// reports/api.js - FIXED VERSION with Category Export Fix
import axios from 'axios';
import Base_url from '../config';
import Auth from '../Auth/Auth';

const API_BASE_URL = `${Base_url}`;

// Create axios instance with base configuration
const reportsAPI = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
reportsAPI.interceptors.request.use(
  (config) => {
    const authHeaders = Auth.getAuthHeaders();
    config.headers = { ...config.headers, ...authHeaders };
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle authentication errors
reportsAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      Auth.clearAuth();
    }
    return Promise.reject(error);
  }
);

// Helper function to check authentication
const requireAuth = () => {
  if (!Auth.isAuthenticated()) {
    throw new Error('User not authenticated');
  }
};

// Helper function to ensure array response
const ensureArray = (data, arrayKey = null) => {
  if (Array.isArray(data)) {
    return data;
  }
  
  if (data && typeof data === 'object') {
    if (arrayKey && Array.isArray(data[arrayKey])) {
      return data[arrayKey];
    }
    
    const nestedArrayKeys = [
      'apartments', 'categories', 'tenants', 'results', 'data', 
      'export_requests', 'items', 'list'
    ];
    
    for (const key of nestedArrayKeys) {
      if (Array.isArray(data[key])) {
        return data[key];
      }
    }
    
    const keys = Object.keys(data);
    if (keys.length > 0 && keys.every(key => !isNaN(key))) {
      return Object.values(data);
    }
    
    return [data];
  }
  
  return [];
};

// FIXED: Format date for API (ensure consistent format)
const formatDateForAPI = (date) => {
  if (!date) return null;
  
  // If it's already a string in YYYY-MM-DD format, return as is
  if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return date;
  }
  
  // Convert to Date object if it's not already
  const dateObj = new Date(date);
  
  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    throw new Error(`Invalid date: ${date}`);
  }
  
  // Return in YYYY-MM-DD format
  return dateObj.toISOString().split('T')[0];
};

// Export Request API
export const exportRequestAPI = {
  // FIXED: Create new export request with proper validation
  create: async (exportData) => {
    try {
      requireAuth();
      
      // FIXED: Validate and format the export data
      const formattedData = { ...exportData };
      
      // Store original values for validation before transformation
      const originalApartmentId = formattedData.selectedApartmentId;
      const originalCategoryId = formattedData.selectedCategoryId;
      const originalTenantId = formattedData.selectedTenantId;
      
      // Format dates properly
      if (formattedData.date_from) {
        formattedData.date_from = formatDateForAPI(formattedData.date_from);
      }
      if (formattedData.date_to) {
        formattedData.date_to = formatDateForAPI(formattedData.date_to);
      }
      
      // FIXED: Ensure apartment selection is properly formatted
      if (formattedData.selectedApartmentId) {
        // Single apartment selection
        formattedData.selected_apartment_id = formattedData.selectedApartmentId;
        delete formattedData.selectedApartmentId;
      } else if (formattedData.selected_apartment_id) {
        // Field is already correctly named, keep it
        formattedData.selected_apartment_id = formattedData.selected_apartment_id;
      }
      
      // Handle multiple apartments if needed
      if (formattedData.apartment_selection === 'selected') {
        
        if (formattedData.selectedApartments && Array.isArray(formattedData.selectedApartments)) {
          // Multiple apartments selection
          formattedData.selected_apartments = formattedData.selectedApartments;
          delete formattedData.selectedApartments;
        }
      }
      
      // FIXED: Handle category selection
      if (formattedData.selectedCategoryId) {
        formattedData.selected_category_id = formattedData.selectedCategoryId;
        delete formattedData.selectedCategoryId;
      } else if (formattedData.selected_category_id) {
        formattedData.selected_category_id = formattedData.selected_category_id;
      }
      
      // FIXED: Handle tenant selection
      if (formattedData.selectedTenantId) {
        formattedData.selected_tenant_id = formattedData.selectedTenantId;
        delete formattedData.selectedTenantId;
      }
      
      // FIXED: Handle boolean flags properly
      if (formattedData.includePayments !== undefined) {
        formattedData.export_payments = Boolean(formattedData.includePayments);
        delete formattedData.includePayments;
      }
      
      if (formattedData.includeTenantDetails !== undefined) {
        formattedData.export_statements = Boolean(formattedData.includeTenantDetails);
        delete formattedData.includeTenantDetails;
      }
      
      if (formattedData.exportStatements !== undefined) {
        formattedData.export_statements = Boolean(formattedData.exportStatements);
        delete formattedData.exportStatements;
      }
      
      if (formattedData.exportLease !== undefined) {
        formattedData.export_lease = Boolean(formattedData.exportLease);
        delete formattedData.exportLease;
      }
      
      // FIXED: Validate required fields based on export type (using original values)
      if (formattedData.export_type === 'category') {
        if (!formattedData.selected_apartment_id && !originalApartmentId) {
          throw new Error('Apartment selection is required for category exports');
        }
        if (!formattedData.selected_category_id && !originalCategoryId) {
          throw new Error('Category selection is required for category exports');
        }
      }
      
      if (formattedData.export_type === 'tenant') {
        // Check if this is a specific tenant export or all tenants export
        const isSpecificTenant = formattedData.selected_tenant_id || originalTenantId;
        const isAllTenants = formattedData.apartment_selection;
        
        // Require either a specific tenant OR apartment selection for all tenants
        if (!isSpecificTenant && !isAllTenants) {
          throw new Error('Either tenant selection or apartment selection is required for tenant exports');
        }
        
        // If apartment_selection is "selected", ensure selected_apartments is provided
        if (formattedData.apartment_selection === 'selected' && 
            (!formattedData.selected_apartments || formattedData.selected_apartments.length === 0)) {
          throw new Error('Selected apartments are required when apartment selection is "selected"');
        }
      }
      
      // FIXED: Validate date range for custom period
      if (formattedData.period_type === 'custom') {
        if (!formattedData.date_from || !formattedData.date_to) {
          throw new Error('Both start and end dates are required for custom period');
        }
        
        const startDate = new Date(formattedData.date_from);
        const endDate = new Date(formattedData.date_to);
        
        if (startDate >= endDate) {
          throw new Error('Start date must be before end date');
        }
      }
      
      console.log('Sending export request:', formattedData);
      
      const response = await reportsAPI.post('/export-requests/', formattedData);
      
      console.log('Export request response:', response);
      
      // Handle different possible response structures
      let exportId;
      const responseData = response.data;
      
      if (responseData?.export_request?.id) {
        exportId = responseData.export_request.id;
      } else if (responseData?.id) {
        exportId = responseData.id;
      } else if (responseData?.data?.id) {
        exportId = responseData.data.id;
      } else {
        console.error('Could not find export ID in response:', responseData);
        throw new Error('Export ID not found in response');
      }
      
      console.log('Extracted export ID:', exportId);
      
      return { 
        success: true, 
        data: {
          ...responseData,
          id: exportId
        }
      };
    } catch (error) {
      console.error('Export request creation failed:', error);
      console.error('Error response:', error.response?.data);
      
      // FIXED: Better error handling
      let errorMessage = 'Failed to create export request';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.non_field_errors) {
          errorMessage = error.response.data.non_field_errors.join(', ');
        } else {
          // Handle field-specific errors
          const fieldErrors = [];
          Object.keys(error.response.data).forEach(field => {
            if (Array.isArray(error.response.data[field])) {
              fieldErrors.push(`${field}: ${error.response.data[field].join(', ')}`);
            }
          });
          if (fieldErrors.length > 0) {
            errorMessage = fieldErrors.join('; ');
          }
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  },

  // Get all export requests
  list: async (params = {}) => {
    try {
      requireAuth();
      const response = await reportsAPI.get('/export-requests/', { params });
      
      const responseData = response.data;
      const exportRequests = ensureArray(responseData, 'results');
      
      return { 
        success: true, 
        data: {
          ...responseData,
          results: exportRequests,
          count: responseData?.count || exportRequests.length,
          next: responseData?.next || null,
          previous: responseData?.previous || null
        }
      };
    } catch (error) {
      console.error('Failed to fetch export requests:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.message 
      };
    }
  },

  // Get specific export request
  get: async (id) => {
    try {
      requireAuth();
      const response = await reportsAPI.get(`/export-requests/${id}/`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error(`Failed to fetch export request ${id}:`, error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.message 
      };
    }
  },

  // Get export progress
  getProgress: async (id) => {
    try {
      requireAuth();
      const response = await reportsAPI.get(`/export-requests/${id}/`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error(`Failed to get export progress for ${id}:`, error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.message 
      };
    }
  },

  // Download export file
  download: async (id) => {
    try {
      requireAuth();
      const response = await reportsAPI.get(`/export-requests/${id}/download/`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'export.xlsx';
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch) {
          filename = fileNameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      console.error(`Failed to download export ${id}:`, error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.message 
      };
    }
  },

  // Delete export request
  delete: async (id) => {
    try {
      requireAuth();
      if (!Auth.isAdmin() && !Auth.isManager()) {
        return {
          success: false,
          error: 'Insufficient permissions to delete export requests'
        };
      }
      
      await reportsAPI.delete(`/export-requests/${id}/`);
      return { success: true };
    } catch (error) {
      console.error(`Failed to delete export request ${id}:`, error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.message 
      };
    }
  },

  // Get apartments for selection
  getApartments: async () => {
    try {
      requireAuth();
      const response = await reportsAPI.get('/export-requests/apartments/');
      
      const responseData = response.data;
      const apartments = ensureArray(responseData, 'apartments');
      
      return { 
        success: true, 
        data: apartments
      };
    } catch (error) {
      console.error('Failed to fetch apartments:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.message 
      };
    }
  },

  // Get categories for selection
  getCategories: async (apartmentId = null) => {
    try {
      requireAuth();
      const params = apartmentId ? { apartment_id: apartmentId } : {};
      const response = await reportsAPI.get('/export-requests/categories/', { params });
      
      const responseData = response.data;
      const categories = ensureArray(responseData, 'categories');
      
      return { 
        success: true, 
        data: categories
      };
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.message 
      };
    }
  },

  // Get tenants for selection
  getTenants: async (searchTerm = '', apartmentId = null) => {
    try {
      requireAuth();
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (apartmentId) params.apartment_id = apartmentId;
      
      const response = await reportsAPI.get('/export-requests/tenants/', { params });
      
      const responseData = response.data;
      const tenants = ensureArray(responseData, 'tenants');
      
      return { 
        success: true, 
        data: tenants
      };
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.message 
      };
    }
  },

  // Clean up expired exports
  cleanupExpired: async () => {
    try {
      requireAuth();
      if (!Auth.isAdmin()) {
        return {
          success: false,
          error: 'Admin permissions required for cleanup operations'
        };
      }
      
      const response = await reportsAPI.delete('/export-requests/cleanup-expired/');
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Failed to cleanup expired exports:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.message 
      };
    }
  },

  // Get export statistics
  getStats: async () => {
    try {
      requireAuth();
      const response = await reportsAPI.get('/export-requests/stats/');
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Failed to fetch export stats:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.message 
      };
    }
  },
};

// Export History API
export const exportHistoryAPI = {
  list: async (params = {}) => {
    try {
      requireAuth();
      const response = await reportsAPI.get('/export-history/', { params });
      
      const responseData = response.data;
      const historyItems = ensureArray(responseData);
      
      return { 
        success: true, 
        data: {
          ...responseData,
          results: historyItems,
          count: responseData?.count || historyItems.length,
          next: responseData?.next || null,
          previous: responseData?.previous || null
        }
      };
    } catch (error) {
      console.error('Failed to fetch export history:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.message 
      };
    }
  },

  getSummary: async () => {
    try {
      requireAuth();
      const response = await reportsAPI.get('/export-history/summary/');
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Failed to fetch export history summary:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.message 
      };
    }
  },
};

// FIXED: Helper functions for export requests
export const exportHelpers = {
  // FIXED: Create apartment export request with proper field mapping
  exportApartmentData: async (params) => {
    if (!Auth.isAuthenticated()) {
      return { success: false, error: 'Authentication required' };
    }

    const user = Auth.getUser();
    if (params.apartmentSelection === 'all' && !Auth.isAdmin() && !Auth.isManager()) {
      return { 
        success: false, 
        error: 'Insufficient permissions to export all apartment data' 
      };
    }

    // FIXED: Proper field mapping for apartment export
    const exportData = {
      export_type: params.exportType || 'apartment',
      period_type: params.periodType || 'current',
      apartment_selection: params.apartmentSelection || 'all',
    };

    // Add period-specific fields
    if (params.periodType === 'month' && params.monthYear) {
      exportData.month_year = params.monthYear;
    } else if (params.periodType === 'custom') {
      exportData.date_from = params.dateFrom;
      exportData.date_to = params.dateTo;
    }

    // Add apartment selection
    if (params.apartmentSelection === 'selected') {
      if (params.selectedApartmentId) {
        exportData.selectedApartmentId = params.selectedApartmentId;
      }
      if (params.selectedApartments && Array.isArray(params.selectedApartments)) {
        exportData.selectedApartments = params.selectedApartments;
      }
    }

    // Add export options
    if (params.includePayments !== undefined) {
      exportData.includePayments = params.includePayments;
    }
    if (params.includeTenantDetails !== undefined) {
      exportData.includeTenantDetails = params.includeTenantDetails;
    }

    return exportRequestAPI.create(exportData);
  },

  // FIXED: Create category export request
  exportCategoryData: async (params) => {
    if (!Auth.isAuthenticated()) {
      return { success: false, error: 'Authentication required' };
    }
  
    const exportData = {
      export_type: params.exportType || 'category',
      // FIXED: Ensure we're setting the apartment ID correctly
      selected_apartment_id: params.categoryApartment || params.selectedApartmentId,
      selected_category_id: params.selectedCategoryId || params.categoryType,
      export_payments: params.includePayments || false,
      export_tenant_details: params.includeTenantDetails || false,
      export_based_on: params.exportType === 'tenants_based' ? 'tenants' : 'units'
    };
  
    // Add date range if provided
    if (params.dateFrom) exportData.date_from = formatDateForAPI(params.dateFrom);
    if (params.dateTo) exportData.date_to = formatDateForAPI(params.dateTo);
  
    return exportRequestAPI.create(exportData);
  },

  // FIXED: Create tenant export request
  exportTenantData: async (params) => {
    if (!Auth.isAuthenticated()) {
      return { success: false, error: 'Authentication required' };
    }
  
    if (Auth.isCaretaker && Auth.isCaretaker()) {
      return { 
        success: false, 
        error: 'Insufficient permissions to export tenant data' 
      };
    }
  
    const exportData = {
      export_type: 'tenant',
      export_statements: params.exportStatements || false,
      export_payments: params.exportPayments || false,
      export_lease: params.exportLease || false,
    };
  
    // Add date range if provided
    if (params.date_from) exportData.date_from = params.date_from;
    if (params.date_to) exportData.date_to = params.date_to;
  
    // Handle different tenant selection scenarios
    if (params.selected_tenant_id) {
      // Specific tenant export
      exportData.selected_tenant_id = params.selected_tenant_id;
    } else if (params.apartment_selection) {
      // All tenants export with apartment filtering
      exportData.apartment_selection = params.apartment_selection;
      
      if (params.apartment_selection === 'selected' && params.selected_apartments) {
        exportData.selected_apartments = params.selected_apartments;
      }
    }
  
    console.log('Final export data being sent:', exportData); // Debug log
  
    return exportRequestAPI.create(exportData);
  },

  // Poll export progress
  pollProgress: async (exportId, onProgress, interval = 2000) => {
    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const result = await exportRequestAPI.getProgress(exportId);
          
          if (!result.success) {
            reject(result.error);
            return;
          }

          const exportRequest = result.data.export_request || result.data;
          const { status, progress_percentage, error_message } = exportRequest;
          
          if (onProgress) {
            onProgress({
              status,
              progress: progress_percentage || 0,
              error: error_message
            });
          }

          if (status === 'completed') {
            resolve(exportRequest);
          } else if (status === 'failed') {
            reject(new Error(error_message || 'Export failed'));
          } else {
            setTimeout(poll, interval);
          }
        } catch (error) {
          reject(error);
        }
      };

      poll();
    });
  },

  // Format export request for display
  formatExportRequest: (exportRequest) => {
    const typeDisplay = {
      apartment: 'Apartment Data',
      category: 'Category Data',
      tenant: 'Tenant Data'
    };

    const statusDisplay = {
      pending: 'Pending',
      processing: 'Processing',
      completed: 'Completed',
      failed: 'Failed'
    };

    return {
      ...exportRequest,
      type_display: typeDisplay[exportRequest.export_type] || exportRequest.export_type,
      status_display: statusDisplay[exportRequest.status] || exportRequest.status,
      created_display: new Date(exportRequest.created_at).toLocaleString(),
      file_size_display: exportRequest.file_size_mb ? 
        `${exportRequest.file_size_mb} MB` : 
        'N/A'
    };
  },

  // Permission checks
  canExportAll: () => Auth.isAdmin() || Auth.isManager(),
  canExportTenantData: () => !Auth.isCaretaker || !Auth.isCaretaker(),
  canDeleteExports: () => Auth.isAdmin() || Auth.isManager(),
  canCleanupExpired: () => Auth.isAdmin(),

  // Safe array access helper
  safeArrayAccess: (data, fallback = []) => {
    return ensureArray(data || fallback);
  }
};

export default {
  exportRequestAPI,
  exportHistoryAPI,
  exportHelpers,
};